import { ActionsSection } from './ScorecardActionsSection';
import { cn } from '@/utils';
import { FEATURE_FLAGS } from '@/constants';
import { ScorecardProps } from '../types';
import { scorecardStyles } from '../Scorecard.styles';
import { ScoreDisplaySection } from './ScorecardScoreDisplaySection';
import { SkillLevelSection } from './ScorecardSkillLevelSection';
import { TextContent } from '@/components/Text';
import { TNormalizedRatingCutoffs, TSimulationType } from '@/types';
import React from 'react';
import { ScoreCircle } from '@/components/Score';

export interface VariantDefaultProps {
  actions?: ScorecardProps['actions'];
  actionsLayout?: NonNullable<ScorecardProps['actionsLayout']>;
  className?: string;
  description?: string;
  handleShareWithManager: () => void;
  isLoading?: boolean;
  isPrinting?: boolean;
  maxScore: number;
  score?: number;
  scoreCardId?: number;
  scoreSize: 'compact' | 'standard' | 'large';
  runtimeRatingCutoffs?: TNormalizedRatingCutoffs;
  sessionId?: number;
  sharedWithManager?: boolean;
  showSkillScale?: boolean;
  simulationType?: TSimulationType;
  size: NonNullable<ScorecardProps['size']>;
  skillLevel?: ScorecardProps['level'] | null;
  skillLevelScaleClassName?: string;
  title?: string;
  useAnimatedNumbers?: boolean;
  userId?: string;
}

export const VariantDefault: React.FC<VariantDefaultProps> = ({
  actions,
  actionsLayout,
  className,
  description,
  handleShareWithManager,
  isLoading = false,
  isPrinting = false,
  maxScore,
  score,
  scoreCardId,
  scoreSize,
  runtimeRatingCutoffs,
  sessionId,
  sharedWithManager,
  showSkillScale = true,
  simulationType,
  size,
  skillLevel,
  skillLevelScaleClassName,
  title,
  useAnimatedNumbers = false,
  userId,
}) => {
  return (
    <div className={cn(scorecardStyles({ size }), className)}>
      {FEATURE_FLAGS.ENABLE_SCORECARD_V2 ? (
        <>
          <ScoreCircle
            className="mb-4"
            isLoading={isLoading}
            runtimeRatingCutoffs={runtimeRatingCutoffs}
            simulationType={simulationType}
            value={score}
            valueMax={maxScore}
          />

          <SkillLevelSection
            actions={actions}
            description={description}
            isLoading={isLoading}
            level={skillLevel}
            showSkillScale={false}
          />
        </>
      ) : (
        <>
          <TextContent
            color="neutral-black"
            isLoading={isLoading}
            label={title}
            level={2}
            loadingClassName="h-6 w-36"
            type="headline"
            variant="sectionSubTitle"
          />

          <ScoreDisplaySection
            isLoading={isLoading}
            isPrinting={isPrinting}
            maxScore={maxScore}
            score={score}
            scoreSize={scoreSize}
            useAnimatedNumbers={useAnimatedNumbers}
          />

          <SkillLevelSection
            actions={actions}
            description={description}
            isLoading={isLoading}
            level={skillLevel}
            showSkillScale={showSkillScale}
            skillLevelScaleClassName={skillLevelScaleClassName}
          />
        </>
      )}

      <ActionsSection
        actions={actions}
        actionsLayout={actionsLayout}
        description={description}
        handleShareWithManager={handleShareWithManager}
        isLoading={isLoading}
        scoreCardId={scoreCardId}
        sessionId={sessionId}
        sharedWithManager={sharedWithManager}
        showSkillScale={showSkillScale}
        size={size}
        userId={userId}
      />
    </div>
  );
};

VariantDefault.displayName = 'VariantDefault';

export default VariantDefault;
